import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { axios } from '../libs';
import { User } from '../states/actions';
import { useTranslation } from '../i18n/I18nContext';
import { LEGAL_FIELD_CATEGORIES } from '../constants/legalFieldCategories';
import logger from '../utils/logger';

const EXPERTISE_NOTE_MIN = 40;
const EXPERTISE_NOTE_MAX = 120;

function isExpertiseNoteValid(note) {
	const len = String(note || '').trim().length;
	return len === 0 || (len >= EXPERTISE_NOTE_MIN && len <= EXPERTISE_NOTE_MAX);
}

function normalizeExpertiseForSave(expertise) {
	return (expertise || []).map((item) => ({
		field_id: Number(item.field_id),
		note: String(item.note || '').trim(),
	}));
}

function mapProfileExpertise(expertise) {
	return (expertise || []).map((item) => ({
		field_id: Number(item.field_id),
		note: item.note || '',
	}));
}

export const Profile = () => {
	const { t } = useTranslation();
	const [userProfile, setUserProfile] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const [formData, setFormData] = useState({});
	const [saving, setSaving] = useState(false);
	const [legalFields, setLegalFields] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [expertise, setExpertise] = useState([]);
	const [savedExpertise, setSavedExpertise] = useState([]);
	
	/* Get current user from Redux */
	const currentUser = useSelector(User.get());
	
	/* Auto-update lawyers.json when profile changes */
	const updateLawyerProfileData = async (updatedProfile = null) => {
		// Use updatedProfile if provided, otherwise use userProfile or currentUser
		const user = updatedProfile || userProfile || currentUser;
		
		if (!user || (user.role !== 'lawyer' && user.role !== 'admin')) {
			logger.log('Skipping auto-update: user not found or not lawyer/admin', { user });
			return;
		}

		try {
			logger.log('=== AUTO-UPDATE LAWYERS.JSON DEBUG ===');
			logger.log('Current user object:', user);
			logger.log('is_active_for_matching:', user.is_active_for_matching);
			logger.log('Sending data:', {
				user_id: user._id
			});

			const response = await axios.post('/api/update-lawyer-profile', {
				user_id: user._id
			});
			logger.log('Lawyer profile data updated in matchmaking system:', response);
			logger.log('=== AUTO-UPDATE COMPLETED ===');
		} catch (error) {
			logger.error('=== AUTO-UPDATE ERROR ===');
			logger.error('Error auto-updating lawyer profile data:', error);
			logger.error('Error response:', error.response?.data);
			// Don't show error to user for auto-updates
		}
	};
	
	/* Predefined options for matchmaking */
	const availableLocations = [
		'Tallinn', 'Tartu', 'Pärnu', 'Narva', 'Kohtla-Järve', 
		'Viljandi', 'Rakvere', 'Maardu', 'Sillamäe', 'Kuressaare',
		'Võru', 'Valga', 'Haapsalu', 'Jõhvi', 'Paide'
	];
	
	const availableServices = [
		{ value: 'meeting', label: t('profile.serviceMeeting'), disabled: true },
		{ value: 'video', label: t('profile.serviceVideo'), disabled: true },
		{ value: 'web', label: t('profile.serviceWeb') }
	];
	
	const availableLanguages = [
		{ code: 'et', name: t('profile.langEstonian') },
		{ code: 'en', name: t('profile.langEnglish') },
		{ code: 'ru', name: t('profile.langRussian') }
	];
	
	/* Fetch user profile data and legal fields */
	useEffect(() => {
		const fetchUserProfile = async () => {
			try {
				setLoading(true);
				const { user } = await axios.get('/user'); // Gets current user's profile
				setUserProfile(user);
				setFormData({
					firstName: user.firstName || '',
					lastName: user.lastName || '',
					email: user.email || '',
					role: user.role || '', // Include role as hidden field
					partner_company_name: user.partner_company_name || '',
					partner_IBAN: user.partner_IBAN || '',
					partner_company_address: user.partner_company_address || '',
					partner_hourly_rate: user.partner_hourly_rate || '',
					partner_discount_price: user.partner_discount_price || '',
					partner_vat: user.partner_vat || false,
					partner_company_reg_id: user.partner_company_reg_id || '',
					partner_company_vat_id: user.partner_company_vat_id || '',
					// Business client pricing
					business_client: user.business_client || false,
					business_hourly_rate: user.business_hourly_rate || '',
					business_discount_price: user.business_discount_price || '',
					// Matchmaking fields for lawyers
					legal_fields: user.legal_fields || [],
					services_offered: user.services_offered || [],
					languages: user.languages || [],
					office_locations: user.office_locations || '',
					picture_url: user.picture_url || '',
					videocall_url: user.videocall_url || '',
					rating: user.rating || 0,
					is_active_for_matching: user.is_active_for_matching || false,
					use_gmail_for_emails: user.use_gmail_for_emails !== undefined ? user.use_gmail_for_emails : true,
					locale: user.locale || 'en',
					selected_locations: user.office_locations ? user.office_locations.split(',').map(l => l.trim()).filter(Boolean) : [],
				});
				
			// Fetch legal fields if user is a lawyer or admin (admins can also offer lawyer services)
			if (user.role === 'lawyer' || user.role === 'admin') {
				try {
					const response = await axios.get('/legal-fields');
					logger.log('Legal fields response:', response);
					const fieldsData = response.data || response;
					setLegalFields(Array.isArray(fieldsData) ? fieldsData.filter(field => field.is_active) : []);
				} catch (fieldError) {
					logger.error('Error fetching legal fields:', fieldError);
					setLegalFields([]);
				}
			}

			if (user.role === 'lawyer' && user.user_no) {
				try {
					const { profile } = await axios.get(`/match/profiles/${user.user_no}`);
					const mappedExpertise = mapProfileExpertise(profile?.expertise);
					setExpertise(mappedExpertise);
					setSavedExpertise(mappedExpertise);
				} catch (profileError) {
					logger.error('Error fetching match profile:', profileError);
					setExpertise([]);
					setSavedExpertise([]);
				}
			}
			} catch (error) {
				logger.error('Error fetching user profile:', error);
				setError(t('profile.loadError'));
			} finally {
				setLoading(false);
			}
		};
		
		fetchUserProfile();
	}, []);
	
	/* Handle form input changes */
	const handleInputChange = (field, value) => {
		setFormData(prev => ({
			...prev,
			[field]: value
		}));
		
		// Validate discount price when it changes or when hourly rate changes
		if (field === 'partner_discount_price' || field === 'partner_hourly_rate') {
			const discountPrice = field === 'partner_discount_price' ? value : formData.partner_discount_price;
			const hourlyRate = field === 'partner_hourly_rate' ? value : formData.partner_hourly_rate;
			
			if (discountPrice && hourlyRate) {
				const discountNum = parseFloat(discountPrice);
				const hourlyNum = parseFloat(hourlyRate);
				
				if (!isNaN(discountNum) && !isNaN(hourlyNum) && hourlyNum > 0) {
					if (discountNum >= hourlyNum / 2) {
						setValidationErrors(prev => ({
							...prev,
							partner_discount_price: t('profile.validation.discountTooHigh')
						}));
					} else {
						setValidationErrors(prev => {
							const newErrors = { ...prev };
							delete newErrors.partner_discount_price;
							return newErrors;
						});
					}
				}
			} else if (field === 'partner_discount_price' && !discountPrice) {
				// Clear error if discount price is cleared
				setValidationErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors.partner_discount_price;
					return newErrors;
				});
			}
		}
		
		// Clear VAT ID if VAT is unchecked
		if (field === 'partner_vat' && !value) {
			setFormData(prev => ({
				...prev,
				partner_company_vat_id: ''
			}));
		}
	};
	
	/* Save language preference immediately */
	const handleLocaleChange = async (newLocale) => {
		if (!userProfile) return;
		const prevLocale = userProfile.locale || 'en';
		if (newLocale === prevLocale) return;

		setFormData((prev) => ({ ...prev, locale: newLocale }));
		setUserProfile((prev) => ({ ...prev, locale: newLocale }));

		try {
			await axios.put(`/user/${userProfile._id}`, {
				_id: userProfile._id,
				email: userProfile.email,
				role: userProfile.role,
				firstName: userProfile.firstName,
				lastName: userProfile.lastName,
				locale: newLocale,
			});
			await User.forceRefresh();
		} catch (error) {
			setFormData((prev) => ({ ...prev, locale: prevLocale }));
			setUserProfile((prev) => ({ ...prev, locale: prevLocale }));
			window.nError({
				title: t('profile.errorTitle'),
				text: t('profile.languageSaveFailed'),
				icon: 'fa-exclamation-triangle',
			});
		}
	};
	
	/* Save profile changes */
	const handleSave = async () => {
		try {
			setSaving(true);
			setValidationErrors({}); // Clear previous validation errors
			
			// Validate required fields (same as when creating a profile)
			const validationErrors = {};
			
			// Validate firstName (required)
			if (!formData.firstName || !formData.firstName.trim()) {
				validationErrors.firstName = t('profile.validation.firstNameRequired');
			}
			
			// Validate lastName (required)
			if (!formData.lastName || !formData.lastName.trim()) {
				validationErrors.lastName = t('profile.validation.lastNameRequired');
			}
			
			// Validate email (required, but should always be present since it's read-only)
			if (!formData.email || !formData.email.trim()) {
				validationErrors.email = t('profile.validation.emailRequired');
			} else {
				// Validate email format
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				if (!emailRegex.test(formData.email.trim())) {
					validationErrors.email = t('profile.validation.emailInvalid');
				}
			}
			
			// Validate role (required, but should always be present)
			if (!formData.role) {
				validationErrors.role = t('profile.validation.roleRequired');
			}
			
			// Validate company information fields (required for lawyers and admins)
			if (formData.role === 'lawyer' || formData.role === 'admin') {
				// Company Name is required
				if (!formData.partner_company_name || !formData.partner_company_name.trim()) {
					validationErrors.partner_company_name = t('profile.validation.companyNameRequired');
				}
				
				// Registration Code is required
				if (!formData.partner_company_reg_id || !formData.partner_company_reg_id.trim()) {
					validationErrors.partner_company_reg_id = t('profile.validation.regCodeRequired');
				}
				
				// Company Address is required
				if (!formData.partner_company_address || !formData.partner_company_address.trim()) {
					validationErrors.partner_company_address = t('profile.validation.addressRequired');
				}
				
				// IBAN is required
				if (!formData.partner_IBAN || !formData.partner_IBAN.trim()) {
					validationErrors.partner_IBAN = t('profile.validation.ibanRequired');
				}
				
				// Hourly Rate is required
				if (!formData.partner_hourly_rate || !formData.partner_hourly_rate.trim()) {
					validationErrors.partner_hourly_rate = t('profile.validation.hourlyRateRequired');
				}
			}
			
			// Validate matchmaking requirements if active
			if (formData.is_active_for_matching) {
				if (!formData.legal_fields || formData.legal_fields.length === 0) {
					validationErrors.is_active_for_matching = t('profile.validation.legalFieldRequired');
				} else if (!formData.services_offered || formData.services_offered.length === 0) {
					validationErrors.is_active_for_matching = t('profile.validation.serviceRequired');
				} else if (!formData.languages || formData.languages.length === 0) {
					validationErrors.is_active_for_matching = t('profile.validation.languageRequired');
				} else if (!formData.office_locations || formData.office_locations.trim() === '') {
					validationErrors.is_active_for_matching = t('profile.validation.locationRequired');
				}
			}
			
			// Validate business client pricing if enabled
			if (formData.business_client && (userProfile?.role === 'lawyer' || userProfile?.role === 'admin')) {
				if (!formData.business_hourly_rate || !formData.business_hourly_rate.trim()) {
					validationErrors.business_hourly_rate = t('profile.validation.businessHourlyRequired');
				}
			}
			
			// Validate discount price
			if (formData.partner_discount_price && formData.partner_hourly_rate) {
				const discountNum = parseFloat(formData.partner_discount_price);
				const hourlyNum = parseFloat(formData.partner_hourly_rate);
				
				if (!isNaN(discountNum) && !isNaN(hourlyNum) && hourlyNum > 0) {
					if (discountNum >= hourlyNum / 2) {
						validationErrors.partner_discount_price = t('profile.validation.discountTooHigh');
					}
				}
			}
			
			// Validate VAT ID is required if VAT is checked
			if (formData.partner_vat && (!formData.partner_company_vat_id || formData.partner_company_vat_id.trim() === '')) {
				validationErrors.partner_company_vat_id = t('profile.validation.vatIdRequired');
			}

			if (userProfile?.role === 'lawyer') {
				const normalizedExpertise = normalizeExpertiseForSave(expertise);
				if (normalizedExpertise.some((item) => !isExpertiseNoteValid(item.note))) {
					validationErrors.expertise = t('match.expertiseNoteLengthError');
				}
			}
			
			if (Object.keys(validationErrors).length > 0) {
				setValidationErrors(validationErrors);
				if (validationErrors.expertise) {
					window.nError?.({
						title: t('profile.errorTitle'),
						text: validationErrors.expertise,
						icon: 'fa-exclamation-triangle',
					});
				}
				setSaving(false);
				return;
			}
			
			// Prepare data - convert empty strings to undefined for optional fields
			const cleanedData = {
				...formData,
				_id: userProfile._id, // Include user ID as required by validation
				// Convert empty strings to undefined for optional fields
				partner_company_name: formData.partner_company_name?.trim() || undefined,
				partner_IBAN: formData.partner_IBAN?.trim() || undefined,
				partner_company_address: formData.partner_company_address?.trim() || undefined,
				partner_hourly_rate: formData.partner_hourly_rate?.trim() || undefined,
				// Send empty string for discount price when cleared, not undefined, so backend can clear the field
				partner_discount_price: formData.partner_discount_price?.trim() ?? '',
				partner_company_reg_id: formData.partner_company_reg_id?.trim() || undefined,
				partner_company_vat_id: formData.partner_company_vat_id?.trim() || undefined,
				// Business client pricing
				business_client: formData.business_client || false,
				business_hourly_rate: formData.business_hourly_rate?.trim() || undefined,
				business_discount_price: formData.business_discount_price?.trim() ?? '',
				// Explicitly include matchmaking fields
				legal_fields: formData.legal_fields || [],
				services_offered: formData.services_offered || [],
				languages: formData.languages || [],
				office_locations: formData.office_locations || '',
				picture_url: formData.picture_url || undefined,
				videocall_url: formData.videocall_url || undefined,
				is_active_for_matching: formData.is_active_for_matching || false,
				use_gmail_for_emails: formData.use_gmail_for_emails !== undefined ? formData.use_gmail_for_emails : true,
				locale: formData.locale || 'en',
			};
			
			// Lawyers cannot update discount prices - remove from payload
			if (userProfile?.role === 'lawyer') {
				delete cleanedData.partner_discount_price;
				delete cleanedData.business_discount_price;
			}
			
			logger.log('Saving profile data:', cleanedData); // Debug log
			
			// Use the correct API endpoint with user ID
			await axios.put(`/user/${userProfile._id}`, cleanedData);

			if (userProfile?.role === 'lawyer' && userProfile.user_no) {
				const allowedIds = new Set((formData.legal_fields || []).map(Number));
				const expertiseToSave = normalizeExpertiseForSave(expertise)
					.filter((item) => allowedIds.has(item.field_id));
				try {
					await axios.put(`/match/profiles/${userProfile.user_no}`, {
						expertise: expertiseToSave,
					});
					setExpertise(expertiseToSave);
					setSavedExpertise(expertiseToSave);
				} catch (matchError) {
					logger.error('Failed to save match profile:', matchError);
					const matchErrorMessage = matchError.response?.data?.message
						|| matchError.message
						|| t('match.saveFailed')
						|| 'Failed to save expertise';
					window.nError({
						title: t('profile.errorTitle'),
						text: matchErrorMessage,
						icon: 'fa-exclamation-triangle',
					});
					setSaving(false);
					return;
				}
			}
			
			// Update local state
			setUserProfile(prev => ({ ...prev, ...formData }));
			setIsEditing(false);
			
			// Auto-update lawyers.json with new profile data
			logger.log('About to call updateLawyerProfileData...');
			await updateLawyerProfileData();
			logger.log('updateLawyerProfileData completed');
			
			window.nSuccess({
				title: t('profile.successTitle'),
				text: t('profile.updatedSuccess'),
				icon: 'fa-check-square'
			});
			
			// Refresh user data in Redux
			User.forceRefresh();
			
		} catch (error) {
			logger.error('Error updating profile:', error);
			
			// Extract error message from response
			const errorMessage = error.response?.data?.error 
				|| error.response?.data?.message 
				|| error.message 
				|| t('profile.updateFailed');
			
			window.nError({
				title: t('profile.errorTitle'),
				text: errorMessage,
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setSaving(false);
		}
	};
	
	/* Cancel editing */
	const handleCancel = () => {
		// Reset form data to original values
		setFormData({
			firstName: userProfile.firstName || '',
			lastName: userProfile.lastName || '',
			email: userProfile.email || '',
			role: userProfile.role || '', // Include role as hidden field
			partner_company_name: userProfile.partner_company_name || '',
			partner_IBAN: userProfile.partner_IBAN || '',
			partner_company_address: userProfile.partner_company_address || '',
			partner_hourly_rate: userProfile.partner_hourly_rate || '',
			partner_discount_price: userProfile.partner_discount_price || '',
			partner_vat: userProfile.partner_vat || false,
			partner_company_reg_id: userProfile.partner_company_reg_id || '',
			partner_company_vat_id: userProfile.partner_company_vat_id || '',
			// Business client pricing
			business_client: userProfile.business_client || false,
			business_hourly_rate: userProfile.business_hourly_rate || '',
			business_discount_price: userProfile.business_discount_price || '',
			// Matchmaking fields
			legal_fields: userProfile.legal_fields || [],
			services_offered: userProfile.services_offered || [],
			languages: userProfile.languages || [],
			office_locations: userProfile.office_locations || '',
			picture_url: userProfile.picture_url || '',
			videocall_url: userProfile.videocall_url || '',
			rating: userProfile.rating || 0,
			is_active_for_matching: userProfile.is_active_for_matching || false,
			use_gmail_for_emails: userProfile.use_gmail_for_emails !== undefined ? userProfile.use_gmail_for_emails : true,
			locale: userProfile.locale || 'en',
			selected_locations: userProfile.office_locations ? userProfile.office_locations.split(',').map(l => l.trim()).filter(Boolean) : [],
		});
		setExpertise(savedExpertise);
		setIsEditing(false);
	};
	
	/* Handle checkbox array changes (for multi-select) */
	const handleCheckboxArray = (field, value) => {
		const currentValues = formData[field] || [];
		const newValues = currentValues.includes(value)
			? currentValues.filter(v => v !== value)
			: [...currentValues, value];
		handleInputChange(field, newValues);
	};
	
	/* Handle location selection */
	const handleLocationToggle = (location) => {
		const currentLocations = formData.selected_locations || [];
		const newLocations = currentLocations.includes(location)
			? currentLocations.filter(l => l !== location)
			: [...currentLocations, location];
		handleInputChange('selected_locations', newLocations);
		// Also update office_locations as comma-separated string
		handleInputChange('office_locations', newLocations.join(', '));
	};
	
	/* Handle legal field selection/deselection */
	const handleLegalFieldToggle = (fieldId) => {
		const currentFields = formData.legal_fields || [];
		
		if (currentFields.includes(fieldId)) {
			// Remove field
			handleInputChange('legal_fields', currentFields.filter(id => id !== fieldId));
			setExpertise((prev) => prev.filter((item) => Number(item.field_id) !== Number(fieldId)));
		} else {
			// Add field (no limit)
			handleInputChange('legal_fields', [...currentFields, fieldId]);
		}
	};
	
	if (loading) {
		return (
			<div id="profile" className="row table_v2">
				<div className="col p-0">
					<div className="x_panel chatbot-builder">
						<div className="x_content">
							<div style={{ textAlign: 'center', padding: '50px' }}>
								<p>{t('profile.loading')}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
	
	if (error) {
		return (
			<div id="profile" className="row table_v2">
				<div className="col p-0">
					<div className="x_panel chatbot-builder">
						<div className="x_content">
							<div style={{ textAlign: 'center', padding: '50px' }}>
								<p className="text-danger">{error}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
	
	return (
		<div id="profile" className="row table_v2">
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>{t('profile.title')}</h1>
						</div>
						<div className="header-actions d-flex align-items-center">
							{!isEditing ? (
							<>
								<div className="action action-addnew">
									<button className="button button-blue" onClick={() => setIsEditing(true)}>
										{t('profile.editProfile')}
									</button>
								</div>
							</>
							) : (
								<div className="action-group d-flex gap-2">
									<button className="button" onClick={handleCancel} disabled={saving}>
										{t('common.cancel')}
									</button>
									<button 
										className="button button-blue" 
										onClick={handleSave}
										disabled={saving}
									>
										{saving ? t('common.saving') : t('common.save')}
									</button>
								</div>
							)}
						</div>
					</div>
					<div className="x_content">
						<div className="modal-form" style={{paddingTop: '20px', maxWidth: '800px', margin: '0 auto'}}>
							{/* Personal Information Section */}
							<div className="form-row col-full row-label" style={{marginTop: '10px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>{t('profile.personalInformation')}</h3>
							</div>
							
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								{/* Profile Picture Display - Only in not edit mode */}
								{!isEditing && userProfile?.picture_url && (
									<div className="form-row col-full" style={{marginBottom: '24px', padding: '0 10px'}}>
										<img 
											src={userProfile.picture_url} 
											alt="Profile Picture" 
											style={{
												width: '120px', 
												height: '120px', 
												objectFit: 'cover', 
												borderRadius: '50%',
												border: '2px solid #eee'
											}}
										/>
									</div>
								)}
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>First Name <span style={{color: 'red'}}>*</span></label>
											<input
												type="text"
												placeholder="First Name"
												value={formData.firstName}
												onChange={e => handleInputChange('firstName', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.firstName ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.firstName && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.firstName}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>First Name</label>
											<div style={{fontWeight: '500'}}>{userProfile?.firstName || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>Last Name <span style={{color: 'red'}}>*</span></label>
											<input
												type="text"
												placeholder="Last Name"
												value={formData.lastName}
												onChange={e => handleInputChange('lastName', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.lastName ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.lastName && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.lastName}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Last Name</label>
											<div style={{fontWeight: '500'}}>{userProfile?.lastName || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>Email</label>
											<input
												type="text"
												value={userProfile?.email || ''}
												className="readonly"
												readOnly
												style={{width: '100%', backgroundColor: '#f9f9f9'}}
											/>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Email</label>
											<div style={{fontWeight: '500'}}>{userProfile?.email || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>Profile Picture URL</label>
											<input
												type="url"
												placeholder="Profile Picture URL"
												value={formData.picture_url}
												onChange={e => handleInputChange('picture_url', e.target.value)}
												style={{width: '100%'}}
											/>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Profile Picture URL</label>
											<div style={{fontWeight: '500', wordBreak: 'break-all'}}>
												{userProfile?.picture_url ? <a href={userProfile.picture_url} target="_blank" rel="noopener noreferrer">{userProfile.picture_url}</a> : '—'}
											</div>
										</div>
									)}
								</div>
								
						{/* Gmail Email Setting - Only visible for @gmail.com or @hugo.legal emails, not for lawyers */}
						{userProfile?.role !== 'lawyer' && (userProfile?.email?.includes('@gmail.com') || userProfile?.email?.includes('@hugo.legal')) && (
									<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
										{isEditing ? (
											<div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
												<div className="btn-switch" onClick={() => {
													handleInputChange('use_gmail_for_emails', !formData.use_gmail_for_emails);
												}}>
													<div className={`switch ${formData.use_gmail_for_emails ? 'on' : 'off'}`}>
														<span></span>
													</div>
												</div>
												<div style={{flex: 1}}>
													<span style={{fontSize: '13px', color: '#666'}}><strong>Use Gmail account for sending emails</strong></span>
													<p style={{fontSize: '12px', color: '#999', margin: '4px 0 0 0'}}>
														When enabled, emails will be sent from your Gmail account. When disabled, emails will be sent from HUGO Legal (hugo@hugo.legal) with replies going to your email.
													</p>
												</div>
											</div>
										) : (
											<div style={{fontSize: '13px', textAlign: 'left'}}>
												<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Use Gmail for emails</label>
												<div style={{fontWeight: '500'}}>{(userProfile?.use_gmail_for_emails !== undefined ? userProfile.use_gmail_for_emails : true) ? 'Yes' : 'No'}</div>
											</div>
										)}
									</div>
								)}
							</div>
							
							{/* Language Preference Section */}
							<div className="form-row col-full row-label" style={{marginTop: '40px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>{t('profile.languagePreference')}</h3>
							</div>
							<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
								<p style={{fontSize: '13px', color: '#666', marginBottom: '12px'}}>{t('profile.languageDescription')}</p>
								<select
									value={(isEditing ? formData.locale : userProfile?.locale) || 'en'}
									onChange={(e) => handleLocaleChange(e.target.value)}
									style={{width: '100%', maxWidth: '240px'}}
								>
									<option value="en">{t('profile.languageEnglish')}</option>
									<option value="et">{t('profile.languageEstonian')}</option>
								</select>
							</div>
							
							{/* Company Information Section */}
							<div className="form-row col-full row-label" style={{marginTop: '40px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>{t('profile.companyInformation')}</h3>
							</div>
							
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												Company Name {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && <span style={{color: 'red'}}>*</span>}
											</label>
											<input
												type="text"
												placeholder="Company Name"
												value={formData.partner_company_name}
												onChange={e => handleInputChange('partner_company_name', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.partner_company_name ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.partner_company_name && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.partner_company_name}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Company Name</label>
											<div style={{fontWeight: '500'}}>{userProfile?.partner_company_name || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												Registration Code {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && <span style={{color: 'red'}}>*</span>}
											</label>
											<input
												type="text"
												placeholder="Registration Code"
												value={formData.partner_company_reg_id}
												onChange={e => handleInputChange('partner_company_reg_id', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.partner_company_reg_id ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.partner_company_reg_id && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.partner_company_reg_id}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Registration Code</label>
											<div style={{fontWeight: '500'}}>{userProfile?.partner_company_reg_id || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												Company Address {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && <span style={{color: 'red'}}>*</span>}
											</label>
											<input
												type="text"
												placeholder="Company Address"
												value={formData.partner_company_address}
												onChange={e => handleInputChange('partner_company_address', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.partner_company_address ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.partner_company_address && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.partner_company_address}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Company Address</label>
											<div style={{fontWeight: '500'}}>{userProfile?.partner_company_address || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												IBAN {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && <span style={{color: 'red'}}>*</span>}
											</label>
											<input
												type="text"
												placeholder="IBAN"
												value={formData.partner_IBAN}
												onChange={e => handleInputChange('partner_IBAN', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.partner_IBAN ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.partner_IBAN && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.partner_IBAN}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>IBAN</label>
											<div style={{fontWeight: '500'}}>{userProfile?.partner_IBAN || '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												Hourly rate for individual clients {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && <span style={{color: 'red'}}>*</span>}
											</label>
											<input
												type="text"
												placeholder="Hourly Rate"
												value={formData.partner_hourly_rate}
												onChange={e => handleInputChange('partner_hourly_rate', e.target.value)}
												style={{width: '100%', borderColor: validationErrors.partner_hourly_rate ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.partner_hourly_rate && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.partner_hourly_rate}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Hourly rate for individual clients</label>
											<div style={{fontWeight: '500'}}>{userProfile?.partner_hourly_rate ? `€${userProfile.partner_hourly_rate}` : '—'}</div>
										</div>
									)}
								</div>
								
								<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>Discount for 30-min initial contact for individual clients:</label>
											<input
												type="text"
												placeholder="Discount Price"
												value={formData.partner_discount_price}
												onChange={e => handleInputChange('partner_discount_price', e.target.value)}
												disabled={userProfile?.role === 'lawyer'}
												style={{width: '100%', borderColor: validationErrors.partner_discount_price ? '#dc3545' : undefined}}
											/>
											<div style={{minHeight: '20px'}}>
												{validationErrors.partner_discount_price && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.partner_discount_price}
													</div>
												)}
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Discount for 30-min initial contact for individual clients:</label>
											<div style={{fontWeight: '500'}}>{userProfile?.partner_discount_price ? `€${userProfile.partner_discount_price}` : '—'}</div>
										</div>
									)}
								</div>
								
								{/* Business Client Toggle and Pricing - On separate line after hourly rate lines */}
								<div className="form-row col-full" style={{padding: '0 10px', marginTop: '10px', marginBottom: (isEditing ? formData.business_client : userProfile?.business_client) ? '0' : '20px'}}>
									{isEditing ? (
										<div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
											<span style={{fontSize: '13px', color: '#666'}}>Serve Business Clients:</span>
											<div className="btn-switch" onClick={() => handleInputChange('business_client', !formData.business_client)}>
												<div className={`switch ${formData.business_client ? 'on' : 'off'}`}>
													<span></span>
												</div>
											</div>
										</div>
									) : (
										<div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
											<span style={{fontSize: '13px', color: '#666'}}>Serve Business Clients:</span>
											<div className="btn-switch">
												<div className={`switch ${userProfile?.business_client ? 'on' : 'off'}`}>
													<span></span>
												</div>
											</div>
										</div>
									)}
								</div>
								{((isEditing && formData.business_client) || (!isEditing && userProfile?.business_client)) && (
									<>
										<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
											{isEditing ? (
												<>
													<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
														Hourly rate for businesses {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && <span style={{color: 'red'}}>*</span>}
													</label>
													<input
														type="text"
														placeholder="Hourly Rate for Businesses"
														value={formData.business_hourly_rate}
														onChange={e => handleInputChange('business_hourly_rate', e.target.value)}
														style={{width: '100%', borderColor: validationErrors.business_hourly_rate ? '#dc3545' : undefined}}
													/>
													<div style={{minHeight: '20px'}}>
														{validationErrors.business_hourly_rate && (
															<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																{validationErrors.business_hourly_rate}
															</div>
														)}
													</div>
												</>
											) : (
												<div style={{fontSize: '13px', textAlign: 'left'}}>
													<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Hourly rate for businesses</label>
													<div style={{fontWeight: '500'}}>{userProfile?.business_hourly_rate ? `€${userProfile.business_hourly_rate}` : '—'}</div>
												</div>
											)}
										</div>
										
										<div className="form-row col-half" style={{padding: '0 10px', marginBottom: '20px'}}>
											{isEditing ? (
												<>
													<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>Discount for 30-min initial contact for businesses:</label>
													<input
														type="text"
														placeholder="Discount Price for Businesses"
														value={formData.business_discount_price}
														onChange={e => handleInputChange('business_discount_price', e.target.value)}
														disabled={userProfile?.role === 'lawyer'}
														style={{width: '100%', borderColor: validationErrors.business_discount_price ? '#dc3545' : undefined}}
													/>
													<div style={{minHeight: '20px'}}>
														{validationErrors.business_discount_price && (
															<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																{validationErrors.business_discount_price}
															</div>
														)}
													</div>
												</>
											) : (
												<div style={{fontSize: '13px', textAlign: 'left'}}>
													<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Discount for 30-min initial contact for businesses:</label>
													<div style={{fontWeight: '500'}}>{userProfile?.business_discount_price ? `€${userProfile.business_discount_price}` : '—'}</div>
												</div>
											)}
										</div>
									</>
								)}
								
								{/* VAT Toggle and VAT ID - On separate line after prices */}
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: formData.partner_vat ? '15px' : '0'}}>
												<span style={{fontSize: '13px', color: '#666'}}>VAT Applied:</span>
												<div className="btn-switch" onClick={() => handleInputChange('partner_vat', !formData.partner_vat)}>
													<div className={`switch ${formData.partner_vat ? 'on' : 'off'}`}>
														<span></span>
													</div>
												</div>
											</div>
											{formData.partner_vat && (
												<div>
													<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
														VAT ID: <span style={{color: 'red'}}>*</span>
													</label>
													<input
														type="text"
														placeholder="VAT ID"
														value={formData.partner_company_vat_id}
														onChange={e => handleInputChange('partner_company_vat_id', e.target.value)}
														style={{width: '100%', borderColor: validationErrors.partner_company_vat_id ? '#dc3545' : undefined}}
													/>
													<div style={{minHeight: '20px'}}>
														{validationErrors.partner_company_vat_id && (
															<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																{validationErrors.partner_company_vat_id}
															</div>
														)}
													</div>
												</div>
											)}
										</>
									) : (
										<>
											<div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: userProfile?.partner_vat ? '15px' : '0'}}>
												<span style={{fontSize: '13px', color: '#666'}}>VAT Applied:</span>
												<div className="btn-switch">
													<div className={`switch ${userProfile?.partner_vat ? 'on' : 'off'}`}>
														<span></span>
													</div>
												</div>
											</div>
											{userProfile?.partner_vat && (
												<div style={{fontSize: '13px', textAlign: 'left'}}>
													<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>VAT ID:</label>
													<div style={{fontWeight: '500'}}>{userProfile?.partner_company_vat_id || '—'}</div>
												</div>
											)}
										</>
									)}
								</div>
															</div>
							
						{/* Matchmaking Settings Section - For Lawyers and Admins */}
						{(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && (
							<>
									<div className="form-row col-full row-label" style={{marginTop: '50px'}}>
										<h3>{t('profile.matchmakingSettings')}</h3>
									
									</div>
									
								{/* Active for Matching - Only show in edit mode */}
								{isEditing && (
									<div className="form-row col-full" style={{marginTop: '0px'}}>
										<div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: validationErrors.is_active_for_matching ? '5px' : '0'}}>
											<div className="btn-switch" onClick={() => {
												// Check if lawyer is in basic mode
												if (userProfile?.role === 'lawyer' && userProfile?.lawyer_level === 'basic') {
													window.nError({
														title: t('profile.notAvailable'),
														text: t('profile.basicModeUpgrade'),
														icon: 'fa-exclamation-circle',
													});
													return;
												}
												
												const newValue = !formData.is_active_for_matching;
												handleInputChange('is_active_for_matching', newValue);
												// Clear validation error when unchecked
												if (!newValue && validationErrors.is_active_for_matching) {
													setValidationErrors(prev => {
														const newErrors = { ...prev };
														delete newErrors.is_active_for_matching;
														return newErrors;
													});
												}
											}}>
												<div className={`switch ${formData.is_active_for_matching ? 'on' : 'off'}${userProfile?.role === 'lawyer' && userProfile?.lawyer_level === 'basic' ? ' disabled' : ''}`}>
													<span></span>
												</div>
											</div>
											<span style={{fontSize: '13px', color: '#666'}}>Show me to clients in matchmaking results{userProfile?.role === 'lawyer' && userProfile?.lawyer_level === 'basic' ? ' (Can\'t be changed in basic mode)' : ''}</span>
										</div>
										{validationErrors.is_active_for_matching && (
											<div style={{
												marginTop: '5px',
												fontSize: '12px',
												color: '#dc3545'
											}}>
												{validationErrors.is_active_for_matching}
											</div>
										)}
									</div>
								)}
									
									{/* Legal Fields */}
									<div className="form-row col-full" style={{marginTop: '20px'}}>
										<label>Legal Fields ({formData.legal_fields?.length || 0} / {userProfile?.max_legal_fields || 999})</label>
										{isEditing ? (
											<div>
												{legalFields.length > 0 ? (
													<div className="accordion">
														{(() => {
															// Group legal fields by category
															const groupedFields = legalFields
																.sort((a, b) => a.order - b.order)
																.reduce((groups, field) => {
																	const category = field.category || 'Other';
																	if (!groups[category]) {
																		groups[category] = [];
																	}
																	groups[category].push(field);
																	return groups;
																}, {});

															// Sort categories: predefined categories first, then "Other"
															const predefinedCategories = LEGAL_FIELD_CATEGORIES;
															const sortedCategories = [
																...predefinedCategories.filter(cat => groupedFields[cat]),
																...Object.keys(groupedFields).filter(cat => !predefinedCategories.includes(cat)).sort()
															];

															return sortedCategories.map((category, categoryIndex) => (
																<div key={category} className="panel">
																	<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
																		const content = document.getElementById(`legal-fields-${categoryIndex}`);
																		const icon = document.getElementById(`legal-fields-icon-${categoryIndex}`);
																		if (content.style.display === 'none') {
																			content.style.display = 'block';
																			icon.className = 'fa fa-chevron-up';
																		} else {
																			content.style.display = 'none';
																			icon.className = 'fa fa-chevron-down';
																		}
																	}}>
																		<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
																			{category}
																			<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																				{groupedFields[category].length} field{groupedFields[category].length !== 1 ? 's' : ''}
																			</span>
																			<i id={`legal-fields-icon-${categoryIndex}`} className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
																		</h4>
																	</div>
																	<div id={`legal-fields-${categoryIndex}`} style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
																		<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
																			{groupedFields[category].map(field => (
																				<li key={field.field_id} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																					<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0, textAlign: 'left', justifyContent: 'flex-start'}}>
																						<input
																							type="checkbox"
																							className="form-check-input"
																							checked={formData.legal_fields?.includes(field.field_id) || false}
																							onChange={() => handleLegalFieldToggle(field.field_id)}
																							style={{margin: 0, width: '22px', height: '22px'}}
																						/>
																						<span style={{fontSize: '13px'}}>{field.name_et}</span>
																					</label>
																				</li>
																			))}
																		</ul>
																	</div>
																</div>
															));
														})()}
													</div>
												) : (
													<p style={{color: '#999'}}>No legal fields available. Contact admin to add legal fields.</p>
												)}
											</div>
										) : (
											<div>
												{userProfile?.legal_fields && userProfile.legal_fields.length > 0 ? (
													<div style={{fontSize: '13px', textAlign: 'left'}}>
														{userProfile.legal_fields.map(fieldId => {
															const field = legalFields.find(f => f.field_id === fieldId);
															return field ? field.name_et : null;
														}).filter(Boolean).join(', ')}
													</div>
												) : (
													<p style={{color: '#999'}}>No legal fields selected</p>
												)}
											</div>
										)}
									</div>

									{userProfile?.role === 'lawyer' && (
										<div className="form-row col-full" style={{ marginTop: '20px' }}>
											<label>{t('match.expertiseTop5')} ({expertise.length || 0} / 5)</label>
											<p style={{ fontSize: '13px', color: '#666' }}>{t('match.expertiseTop5Desc')}</p>
											{validationErrors.expertise && (
												<div style={{
													marginBottom: '8px',
													fontSize: '12px',
													color: '#dc3545',
												}}>
													{validationErrors.expertise}
												</div>
											)}
											{isEditing ? (
												<div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
													{(formData.legal_fields || []).length > 0 ? (
														(formData.legal_fields || []).map((fieldId) => {
															const fieldIdNum = Number(fieldId);
															const field = legalFields.find((f) => Number(f.field_id) === fieldIdNum);
															const checked = expertise.some((item) => Number(item.field_id) === fieldIdNum);
															const note = expertise.find((item) => Number(item.field_id) === fieldIdNum)?.note || '';
															const noteLen = note.trim().length;
															const noteInvalid = noteLen > 0 && (noteLen < EXPERTISE_NOTE_MIN || noteLen > EXPERTISE_NOTE_MAX);
															return (
																<div key={fieldId} style={{ marginBottom: '12px' }}>
																	<label style={{ display: 'block', marginBottom: '6px' }}>
																		<input
																			type="checkbox"
																			checked={checked}
																			onChange={() => {
																				if (checked) {
																					setExpertise((prev) => prev.filter((item) => Number(item.field_id) !== fieldIdNum));
																				} else if (expertise.length >= 5) {
																					window.nError?.({
																						title: t('profile.errorTitle'),
																						text: t('match.expertiseMax5'),
																						icon: 'fa-exclamation-triangle',
																					});
																				} else {
																					setExpertise((prev) => [...prev, { field_id: fieldIdNum, note: '' }]);
																				}
																			}}
																		/>
																		{' '}{field?.name_et || fieldId}
																	</label>
																	{checked && (
																		<div style={{ marginLeft: '20px' }}>
																			<label style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
																				{t('match.expertiseNoteOptional')}
																			</label>
																			<textarea
																				className="form-control"
																				rows={2}
																				value={note}
																				onChange={(e) => {
																					const value = e.target.value;
																					setExpertise((prev) => prev.map((item) =>
																						Number(item.field_id) === fieldIdNum ? { ...item, note: value } : item
																					));
																				}}
																			/>
																			<small style={{ color: noteInvalid ? '#c00' : '#666' }}>
																				{noteLen} / {EXPERTISE_NOTE_MAX}{noteInvalid ? ` — ${t('match.expertiseNoteLengthError')}` : ''}
																			</small>
																		</div>
																	)}
																</div>
															);
														})
													) : (
														<p style={{ color: '#999' }}>No legal fields selected</p>
													)}
												</div>
											) : (
												<div>
													{expertise.length > 0 ? (
														<div style={{ fontSize: '13px', textAlign: 'left' }}>
															{expertise.map((item) => {
																const field = legalFields.find((f) => Number(f.field_id) === Number(item.field_id));
																return (
																	<div key={item.field_id} style={{ marginBottom: '8px' }}>
																		<div style={{ fontWeight: '500' }}>{field?.name_et || item.field_id}</div>
																		{item.note?.trim() && (
																			<div style={{ color: '#666', marginTop: '2px' }}>{item.note.trim()}</div>
																		)}
																	</div>
																);
															})}
														</div>
													) : (
														<p style={{ color: '#999' }}>No expertise fields selected</p>
													)}
												</div>
											)}
										</div>
									)}
									
								{/* Services, Locations and Languages */}
								<div className="form-row col-full" style={{marginTop: '20px'}}>
									<label>Services, Locations and Languages</label>
									{isEditing ? (
										<div>
											{/* Services Offered */}
											<div className="accordion">
												<div className="panel">
													<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
														const content = document.getElementById('services-offered-content');
														const icon = document.getElementById('services-offered-icon');
														if (content.style.display === 'none') {
															content.style.display = 'block';
															icon.className = 'fa fa-chevron-up';
														} else {
															content.style.display = 'none';
															icon.className = 'fa fa-chevron-down';
														}
													}}>
														<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
															Services Offered
															<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																{formData.services_offered?.length || 0} selected
															</span>
															<i id="services-offered-icon" className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
														</h4>
													</div>
													<div id="services-offered-content" style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
														<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
															{availableServices.map(service => (
																<li key={service.value} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																	<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: service.disabled ? 'not-allowed' : 'pointer', justifyContent: 'flex-start'}}>
																		<input
																			type="checkbox"
																			className="form-check-input"
																			checked={formData.services_offered?.includes(service.value) || false}
																			onChange={() => !service.disabled && handleCheckboxArray('services_offered', service.value)}
																			disabled={service.disabled}
																			style={{margin: 0, width: '22px', height: '22px', opacity: service.disabled ? 0.5 : 1}}
																		/>
																		<span style={{fontSize: '13px', opacity: service.disabled ? 0.5 : 1}}>{service.label}</span>
																	</label>
																</li>
															))}
														</ul>
													</div>
												</div>
											</div>

											{/* Languages */}
											<div className="accordion">
												<div className="panel">
													<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
														const content = document.getElementById('languages-content');
														const icon = document.getElementById('languages-icon');
														if (content.style.display === 'none') {
															content.style.display = 'block';
															icon.className = 'fa fa-chevron-up';
														} else {
															content.style.display = 'none';
															icon.className = 'fa fa-chevron-down';
														}
													}}>
														<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
															Languages
															<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																{formData.languages?.length || 0} selected
															</span>
															<i id="languages-icon" className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
														</h4>
													</div>
													<div id="languages-content" style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
														<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
															{availableLanguages.map(lang => (
																<li key={lang.code} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																	<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'flex-start'}}>
																		<input
																			type="checkbox"
																			className="form-check-input"
																			checked={formData.languages?.includes(lang.code) || false}
																			onChange={() => handleCheckboxArray('languages', lang.code)}
																			style={{margin: 0, width: '22px', height: '22px'}}
																		/>
																		<span style={{fontSize: '13px'}}>{lang.name}</span>
																	</label>
																</li>
															))}
														</ul>
													</div>
												</div>
											</div>

											{/* Office Locations */}
											<div className="accordion">
												<div className="panel">
													<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
														const content = document.getElementById('locations-content');
														const icon = document.getElementById('locations-icon');
														if (content.style.display === 'none') {
															content.style.display = 'block';
															icon.className = 'fa fa-chevron-up';
														} else {
															content.style.display = 'none';
															icon.className = 'fa fa-chevron-down';
														}
													}}>
														<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
															Office Locations
															<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																{formData.selected_locations?.length || 0} selected
															</span>
															<i id="locations-icon" className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
														</h4>
													</div>
													<div id="locations-content" style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
														<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
															{availableLocations.map(location => (
																<li key={location} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																	<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'flex-start'}}>
																		<input
																			type="checkbox"
																			className="form-check-input"
																			checked={formData.selected_locations?.includes(location) || false}
																			onChange={() => handleLocationToggle(location)}
																			style={{margin: 0, width: '22px', height: '22px'}}
																		/>
																		<span style={{fontSize: '13px'}}>{location}</span>
																	</label>
																</li>
															))}
														</ul>
													</div>
												</div>
											</div>
										</div>
									) : (
										<div>
											<div style={{marginBottom: '10px'}}>
												<strong>Services:</strong> {userProfile?.services_offered?.map(s => {
													const service = availableServices.find(srv => srv.value === s);
													return service ? service.label : s;
												}).join(', ') || 'None'}
											</div>
											<div style={{marginBottom: '10px'}}>
												<strong>Languages:</strong> {userProfile?.languages?.map(code => {
													const lang = availableLanguages.find(l => l.code === code);
													return lang ? lang.name : code;
												}).join(', ') || 'None'}
											</div>
											<div>
												<strong>Office Locations:</strong> {userProfile?.office_locations || 'None'}
											</div>
										</div>
									)}
								</div>
									
									
									
									{/* Videocall URL */}
									<div className="form-row col-full">
										{isEditing ? (
											<>
												<label>Videocall URL</label>
										<input
											type="text"
													placeholder="https://meet.google.com/your-link"
													value={formData.videocall_url}
													onChange={e => handleInputChange('videocall_url', e.target.value)}
												/>
												{/* Add spacing after videocall URL in edit mode */}
												<div style={{marginTop: '60px'}}></div>
											</>
										) : (
											<>
												<div style={{fontSize: '13px', textAlign: 'left'}}>
													<strong>Videocall URL:</strong> {userProfile?.videocall_url || 'Not specified'}
												</div>
												{/* Add spacing after videocall URL in read mode */}
												<div style={{marginTop: '60px'}}></div>
											</>
										)}
									</div>
									</>
								)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
